import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface EditOverlayProps {
  progress: number;
}

const CIRCLE_SIZE = 72;
const STROKE_WIDTH = 3;
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function EditOverlay({ progress }: EditOverlayProps) {
  const offset = CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE;
  const { language } = useAuth();
  const lang = language as Language;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl bg-background/60 backdrop-blur-sm"
    >
      <div className="flex flex-col items-center gap-3">
        {/* Circular progress around icon */}
        <div className="relative" style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}>
          {/* Track */}
          <svg
            className="absolute inset-0"
            width={CIRCLE_SIZE}
            height={CIRCLE_SIZE}
            viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
          >
            <circle
              cx={CIRCLE_SIZE / 2}
              cy={CIRCLE_SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={STROKE_WIDTH}
              className="text-muted/40"
            />
          </svg>
          {/* Progress arc */}
          <svg
            className="absolute inset-0 -rotate-90"
            width={CIRCLE_SIZE}
            height={CIRCLE_SIZE}
            viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
          >
            <circle
              cx={CIRCLE_SIZE / 2}
              cy={CIRCLE_SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              className="transition-all duration-300 ease-out"
            />
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4368E5" />
                <stop offset="100%" stopColor="#4368E5" />
              </linearGradient>
            </defs>
          </svg>
          {/* Center icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Sparkles className="h-6 w-6 text-fluxi" />
            </motion.div>
          </div>
        </div>

        {/* Label */}
        <p className="text-xs text-muted-foreground text-center font-medium">
          {dictionary["editing-image-progress"]?.[lang] || "Editing image..."} {Math.round(progress)}%
        </p>
      </div>
    </motion.div>
  );
}
